import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchScan } from '../../actions/scan';
import { fetchSymbolIfNeeded } from '../../actions/symbol';
import useLocalize from '../../utils/hooks/useLocalize';
import ScanResultsTable from './ScanResultsTable';
import scanResultController from './scanResultController';

function shouldFetchField(field, scanData) {
  const key = field?.id?.toLowerCase();
  return Boolean(field?.id) && !scanData?.[key]?.symbols;
}

const ScanResultsSection = () => {
  const localize = useLocalize();
  const dispatch = useDispatch();

  const selectedScan = useSelector((state) => state.scans.selectedScan || null);
  const scanData = useSelector((state) => state.scans.scanData || {});
  const enabledScanCriteria = useSelector(
    (state) => state.scans.enabledScanCriteria || {}
  );

  const selectedFields = selectedScan?.fields;
  const fields = useMemo(() => selectedFields ?? [], [selectedFields]);

  useEffect(() => {
    if (!fields || fields.length === 0) {
      return;
    }

    fields.forEach((field) => {
      if (shouldFetchField(field, scanData)) {
        dispatch(fetchScan(field.id));
      }
    });
  }, [dispatch, fields, scanData]);

  const isLoading = useMemo(() => {
    if (!fields || fields.length === 0) {
      return false;
    }

    return fields.some((field) => shouldFetchField(field, scanData));
  }, [fields, scanData]);

  // Compute filtered combined results from fetched scan datasets
  const scanResults = useMemo(() => {
    return scanResultController.getCombinedScanResults(
      scanData,
      enabledScanCriteria,
      fields
    );
  }, [fields, scanData, enabledScanCriteria]);

  // Fetch symbol metadata for each symbol in the results
  const symbolsData = useSelector((state) => state.symbols.data || {});

  useEffect(() => {
    if (scanResults && scanResults.length > 0) {
      scanResults.forEach((result) => {
        if (result.symbol) {
          dispatch(fetchSymbolIfNeeded(result.symbol));
        }
      });
    }
  }, [dispatch, scanResults]);

  const symbolsMetadata = useMemo(() => {
    const map = {};
    if (scanResults) {
      scanResults.forEach((result) => {
        if (result.symbol && symbolsData[result.symbol]) {
          map[result.symbol] = symbolsData[result.symbol].data || {};
        }
      });
    }
    return map;
  }, [scanResults, symbolsData]);

  return (
    <div className="scan-results-section">
      <h3>{localize('scan.result.title') || 'Scan Results'}</h3>
      <ScanResultsTable
        scanResults={scanResults}
        isLoading={isLoading}
        symbolsMetadata={symbolsMetadata}
      />
    </div>
  );
};

export default ScanResultsSection;
